import { useEffect, useState } from 'react';
import { Trash } from 'lucide-react';
import MarkdownEditor from './MarkdownEditor';
import { GAME_HOST_UNLOCK_PRODUCT_ID, LEVYRAATI_HOST_UNLOCK_PRODUCT_ID } from './profileHelpers';
import { useCreditProductUnlocked } from '../../useStatisticsUnlock';
import EventCalendarActions from './EventCalendarActions';
import { VIRTUAL_ROOM_COUNT } from '../../config/virtualRooms';
import { LIVESTREAM_GENRES, LIVESTREAM_LANGUAGES } from '../../config/livestreamConstants';
import { useT } from '../../i18n/I18nModule';

const API = process.env.REACT_APP_API_URL || '';
const DAY_START = 7 * 60;
const DAY_END = 23 * 60;

function timeToMinutes(timeStr) {
  if (!timeStr) return 0;
  const [hh, mm = 0] = timeStr.split(':').map((x) => parseInt(x, 10) || 0);
  return hh * 60 + mm;
}

function hasAvailableSlot(eventsOnDay, durationMinutes) {
  const occupied = eventsOnDay
    .filter((ev) => ev.time != null)
    .map((ev) => ({
      start: timeToMinutes(ev.time),
      end: timeToMinutes(ev.time) + (ev.minutes || 0),
    }))
    .sort((a, b) => a.start - b.start);
  let cursor = DAY_START;
  for (const block of occupied) {
    if (block.start - cursor >= durationMinutes) return true;
    cursor = Math.max(cursor, block.end);
  }
  return DAY_END - cursor >= durationMinutes;
}

function durationHoursFromMinutes(minutes) {
  const h = minutes ? Math.round(minutes / 60) : 1;
  return Math.min(3, Math.max(1, h));
}

export default function EditEventForm({ event: initialEvent, user, onCancel, onSaved }) {
  const tLabel = useT();
  const [eventName, setEventName] = useState(initialEvent?.title ?? '');
  const rawPlaylist = initialEvent?.levyraati_playlist ?? initialEvent?.levyraatiPlaylist;
  const [eventType, setEventType] = useState(() => {
    const t = initialEvent?.eventType;
    if (t === 'stream') return 'keskustelu';
    return t || (initialEvent?.embedUrl ? 'keskustelu' : 'game');
  });
  const [embedUrl, setEmbedUrl] = useState(initialEvent?.embedUrl ?? '');
  const [description, setDescription] = useState(initialEvent?.description ?? '');
  const [enableQA, setEnableQA] = useState(!!initialEvent?.enableQA);
  const [eventsOnDay, setEventsOnDay] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [gameType, setGameType] = useState(initialEvent?.gameType || 'none');
  const [levyraatiPlaylist, setLevyraatiPlaylist] = useState(Array.isArray(rawPlaylist) ? rawPlaylist.map((t, i) => ({ ...t, position: i })) : []);
  const [levyraatiTrackUrl, setLevyraatiTrackUrl] = useState('');
  const [levyraatiResolving, setLevyraatiResolving] = useState(false);
  const [levyraatiResolveError, setLevyraatiResolveError] = useState(null);
  const [levyraatiResolveHint, setLevyraatiResolveHint] = useState(null);
  const [levyraatiRandomLoading, setLevyraatiRandomLoading] = useState(false);
  const [movieId, setMovieId] = useState(initialEvent?.movieId || initialEvent?.movie_id || '');
  const [movies, setMovies] = useState([]);
  const [roomNumber, setRoomNumber] = useState(initialEvent?.roomNumber ?? initialEvent?.room_number ?? 1);
  const [livestreamType, setLivestreamType] = useState(initialEvent?.livestreamType || initialEvent?.livestream_type || 'music');
  const [livestreamCategory, setLivestreamCategory] = useState(
    initialEvent?.livestreamCategory || initialEvent?.livestream_category || 'pop',
  );

  const { loading: gameHostUnlockLoading, unlocked: gameHostUnlocked } = useCreditProductUnlocked(
    user?.id,
    GAME_HOST_UNLOCK_PRODUCT_ID,
  );
  const { loading: levyraatiHostUnlockLoading, unlocked: levyraatiHostUnlocked } = useCreditProductUnlocked(
    user?.id,
    LEVYRAATI_HOST_UNLOCK_PRODUCT_ID,
  );
  const isAdmin = user?.role === 'admin';
  const peliOptionUnlockedForEdit =
    gameHostUnlocked || eventType === 'game' || initialEvent?.eventType === 'game';
  const levyraatiOptionUnlockedForEdit =
    levyraatiHostUnlocked || eventType === 'levyraati' || initialEvent?.eventType === 'levyraati';
  const canShowMovieOption =
    isAdmin || eventType === 'movie' || initialEvent?.eventType === 'movie';

  const eventId = initialEvent?.id;
  const eventDate = initialEvent?.eventDate ?? '';
  const eventTime = initialEvent?.time ?? '';
  const durationHours = durationHoursFromMinutes(initialEvent?.minutes);

  const handleEventDelete = () => {
    if (!eventId) return;
    const eventTitle = initialEvent?.title ? `"${initialEvent.title}"` : 'tapahtuman';
    const confirmed = window.confirm(`Haluatko varmasti poistaa tapahtuman ${eventTitle}? Tätä toimintoa ei voi perua.`);
    if (!confirmed) return;
    fetch(`${API}/api/events/${eventId}`, { method: 'PATCH' })
      .then(async (res) => {
        const text = await res.text();
        if (!res.ok) {
          let errMsg = 'Failed to delete event';
          try {
            const data = text ? JSON.parse(text) : {};
            errMsg = data.error || errMsg;
          } catch (_) { /* response was not JSON */ }
          throw new Error(errMsg);
        }
        try {
          return text ? JSON.parse(text) : null;
        } catch (_) {
          return null;
        }
      })
      .then(() => {
        setEventsOnDay([]);
        window.location.href = '/';
      })
      .catch((err) => {
        setError(err.message || 'Failed to delete event');
      });
  };

  useEffect(() => {
    if (!eventDate) {
      setEventsOnDay([]);
      return;
    }
    fetch(`${API}/api/events?date=${eventDate}`)
      .then((res) => res.json())
      .then((data) => setEventsOnDay(Array.isArray(data) ? data : []))
      .catch(() => setEventsOnDay([]));
  }, [eventDate]);

  useEffect(() => {
    fetch(`${API}/api/movies`)
      .then((res) => res.json())
      .then((data) => {
        setMovies(Array.isArray(data) ? data : []);
      })
      .catch(() => setMovies([]));
  }, []);

  // Keep gameType and levyraati playlist in sync when the event is loaded or refreshed
  useEffect(() => {
    if (!initialEvent) return;
    const current = initialEvent.gameType || 'none';
    setGameType(current);
    if (initialEvent.eventType === 'levyraati') {
      const pl = initialEvent.levyraati_playlist ?? initialEvent.levyraatiPlaylist;
      setLevyraatiPlaylist(Array.isArray(pl) ? pl.map((t, i) => ({ ...t, position: i })) : []);
    }
  }, [initialEvent]);

  useEffect(() => {
    if (gameHostUnlockLoading) return;
    if (!gameHostUnlocked && eventType === 'game' && initialEvent?.eventType !== 'game') {
      setEventType('keskustelu');
      setGameType(initialEvent?.gameType || 'none');
    }
  }, [gameHostUnlockLoading, gameHostUnlocked, eventType, initialEvent?.eventType, initialEvent?.gameType]);

  useEffect(() => {
    if (levyraatiHostUnlockLoading) return;
    if (!levyraatiHostUnlocked && eventType === 'levyraati' && initialEvent?.eventType !== 'levyraati') {
      setEventType('keskustelu');
    }
  }, [levyraatiHostUnlockLoading, levyraatiHostUnlocked, eventType, initialEvent?.eventType]);

  useEffect(() => {
    if (!isAdmin && eventType === 'movie' && initialEvent?.eventType !== 'movie') {
      setEventType('keskustelu');
      setMovieId(initialEvent?.movieId || initialEvent?.movie_id || '');
      setGameType(initialEvent?.gameType || 'none');
    }
  }, [isAdmin, eventType, initialEvent?.eventType, initialEvent?.gameType, initialEvent?.movieId, initialEvent?.movie_id]);

  const othersOnDay = eventId ? eventsOnDay.filter((ev) => ev.id !== eventId) : eventsOnDay;
  const durationMinutes = durationHours * 60;
  const slotAvailable = eventDate && hasAvailableSlot(othersOnDay, durationMinutes);
  const isYouTubeUrl = (url) => {
    const t = (url || '').trim();
    if (!t) return true;
    return /youtu\.be\//i.test(t) || /youtube\.com\//i.test(t);
  };
  const embedUrlInvalid =
    eventType === 'keskustelu' && embedUrl.trim() !== '' && !isYouTubeUrl(embedUrl);
  const formValid =
    eventName.trim() !== '' &&
    eventDate !== '' &&
    slotAvailable &&
    !embedUrlInvalid;

  const handleReturn = () => {
    if (typeof onCancel === 'function') {
      onCancel();
    } else {
      window.location.href = '/';
    }
  };

  const slotSeconds = durationMinutes * 60;
  const levyraatiDurationRoundedUp = (sec) => Math.ceil((Number(sec) || 0) / 60) * 60;
  const levyraatiTotalSeconds = levyraatiPlaylist.reduce((s, t) => s + levyraatiDurationRoundedUp(t.duration_seconds), 0);
  const levyraatiCanAdd = eventType === 'levyraati' && levyraatiTrackUrl.trim() && levyraatiTotalSeconds < slotSeconds;

  const handleEventTypeChange = (e) => {
    const value = e.target.value;
    setEventType(value);
    if (value === 'keskustelu') {
      setGameType('none');
    } else if (value === 'game') {
      setEmbedUrl('');
    } else if (value === 'levyraati') {
      setEmbedUrl('');
      setLevyraatiResolveError(null);
      setLevyraatiResolveHint(null);
    } else if (value === 'movie') {
      setEmbedUrl('');
      setGameType('none');
    } else if (value === 'livestream') {
      setEmbedUrl('');
      setGameType('none');
      setLivestreamType('music');
      setLivestreamCategory('pop');
    }
  };

  const handleLevyraatiResolve = () => {
    const url = levyraatiTrackUrl.trim();
    if (!url || levyraatiResolving) return;
    if (levyraatiTotalSeconds >= slotSeconds) return;
    setLevyraatiResolveError(null);
    setLevyraatiResolveHint(null);
    setLevyraatiResolving(true);
    fetch(`${API}/api/levyraati/resolve-track`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ url }),
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.error) {
          setLevyraatiResolveError(data.error);
          return;
        }
        const dur = Number(data.duration_seconds) || 0;
        const durRounded = levyraatiDurationRoundedUp(dur);
        if (levyraatiTotalSeconds + durRounded > slotSeconds) {
          setLevyraatiResolveError('Listan kokonaiskesto ylittäisi slotin.');
          return;
        }
        setLevyraatiPlaylist((prev) => [
          ...prev,
          { position: prev.length, source: data.source || '', url, track_author: data.track_author || '', track_name: data.track_name || '', duration_seconds: dur },
        ]);
        setLevyraatiTrackUrl('');
        setLevyraatiResolveHint(data.duration_hint || null);
      })
      .catch(() => setLevyraatiResolveError('URL:n tulkinta epäonnistui.'))
      .finally(() => setLevyraatiResolving(false));
  };

  const removeLevyraatiTrack = (index) => {
    setLevyraatiPlaylist((prev) => prev.filter((_, i) => i !== index).map((t, i) => ({ ...t, position: i })));
  };

  const handleLevyraatiAdd10Random = () => {
    if (levyraatiRandomLoading || eventType !== 'levyraati') return;
    if (levyraatiTotalSeconds >= slotSeconds) return;
    setLevyraatiResolveError(null);
    setLevyraatiRandomLoading(true);
    fetch(`${API}/api/tracks/feeling-lucky?limit=10`)
      .then((res) => res.json())
      .then((data) => {
        if (data.error) {
          setLevyraatiResolveError(data.error);
          return;
        }
        const list = Array.isArray(data.tracks) ? data.tracks : [];
        setLevyraatiPlaylist((prev) => {
          let total = prev.reduce((s, t) => s + levyraatiDurationRoundedUp(t.duration_seconds), 0);
          const added = [];
          for (const t of list) {
            const dur = Number(t.duration_seconds) || 0;
            const durRounded = levyraatiDurationRoundedUp(dur);
            if (total + durRounded > slotSeconds) break;
            total += durRounded;
            added.push({
              position: prev.length + added.length,
              source: 'audius',
              url: t.url || '',
              track_author: t.track_author || '',
              track_name: t.track_name || '',
              duration_seconds: dur,
            });
          }
          return [...prev, ...added];
        });
      })
      .catch(() => setLevyraatiResolveError('Satunnaisten biisien haku epäonnistui.'))
      .finally(() => setLevyraatiRandomLoading(false));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formValid || saving || !eventId) return;
    setError(null);
    setSaving(true);
    fetch(`${API}/api/events/${eventId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        title: eventName.trim(),
        eventDate,
        durationHours,
        embedUrl: eventType === 'keskustelu' ? embedUrl.trim() : '',
        description: description.trim() || null,
        enableQA,
        eventType,
        roomNumber,
        ...(eventType === 'livestream' && { livestreamType, livestreamCategory }),
        movieId: eventType === 'movie' && movieId ? movieId : undefined,
        gameType: !gameType || gameType === 'none' ? 'none' : gameType,
        ...(eventType === 'levyraati' && { levyraati_playlist: levyraatiPlaylist }),
      }),
    })
      .then((res) => {
        if (!res.ok) return res.json().then((err) => { throw new Error(err.error || 'Failed to update event'); });
        return res.json();
      })
      .then((updatedEvents) => {
        setSaving(false);
        if (typeof onSaved === 'function') onSaved(updatedEvents);
      })
      .catch((err) => {
        setError(err.message || 'Failed to save');
        setSaving(false);
      });
  };

  if (!initialEvent) return null;

  return (
    <div className="EditEvent-view-form">
      <h2>Muokkaa tapahtumaa</h2>
      <form className="CreateEvent-form" onSubmit={handleSubmit}>
        <label htmlFor="eventName" className="CreateEvent-field-required">
            Tapahtuman nimi
          </label>

          <input
            id="eventName"
            type="text"
            value={eventName}
            onChange={(e) => setEventName(e.target.value)}
            placeholder="Tapahtuman nimi"
            required
          />
          <label htmlFor="roomNumber" className="CreateEvent-field-required">
            Virtuaalihuone
          </label>
          <select
            id="roomNumber"
            value={roomNumber}
            onChange={(e) => setRoomNumber(Number(e.target.value))}
            className="CreateEvent-time-select"
            required
            aria-label="Virtuaalihuone"
          >
            {Array.from({ length: VIRTUAL_ROOM_COUNT }, (_, i) => i + 1).map((n) => (
            <option key={n} value={n}>
              {tLabel('event.room')} {n}
            </option>
            ))}
          </select>
          <div className="CreateEvent-date-time-duration">
            <div className='date-time-duration-col'>
              <span className="CreateEvent-date-row">PVM: {eventDate}</span>
              <span className="CreateEvent-date-row">alkaa: {eventTime}</span>
              <span className="CreateEvent-date-row">Kesto: {durationHours} t</span>
            </div>
            <div className='date-time-duration-col'>
              <button
                type="button"
                className="CreateEvent-delete-btn CreateEvent-date-time-delete"
                onClick={handleEventDelete}
                title="Tapahtuman aikaa ei voi muutta. Luo uusi tapahtuma ja poista tämä."
                aria-label="Poista tapahtuma"
              >
                <Trash size={60} strokeWidth={4} />
              </button>
            </div>
          </div>

        <label htmlFor="eventType" className="CreateEvent-section-heading CreateEvent-field-required">
          Tapahtuman tyyppi
        </label>
        <select
          id="eventType"
          value={eventType}
          onChange={handleEventTypeChange}
          className="CreateEvent-time-select"
          required
          aria-label="Tapahtuman tyyppi"
        >
          <option value="keskustelu">keskustelu</option>
          <option value="game" disabled={!peliOptionUnlockedForEdit}>
            peli
          </option>
          <option value="levyraati" disabled={!levyraatiOptionUnlockedForEdit}>
            levyraati
          </option>
          {canShowMovieOption ? <option value="movie">elokuva</option> : null}
          <option value="livestream">livestriimi</option>
        </select>
        {eventType === 'livestream' && (
          <>
            <label htmlFor="livestreamType" className="CreateEvent-field">
              Livestriimin tyyppi
            </label>
            <select
              id="livestreamType"
              value={livestreamType}
              onChange={(e) => {
                const t = e.target.value;
                setLivestreamType(t);
                setLivestreamCategory(t === 'talk' ? 'en' : 'pop');
              }}
              className="CreateEvent-select"
            >
              <option value="music">Musiikki</option>
              <option value="talk">Puhe</option>
            </select>
            <label htmlFor="livestreamCategory" className="CreateEvent-field">
              {livestreamType === 'music' ? 'Genre' : 'Kieli'}
            </label>
            <select
              id="livestreamCategory"
              value={livestreamCategory}
              onChange={(e) => setLivestreamCategory(e.target.value)}
              className="CreateEvent-select"
            >
              {(livestreamType === 'music' ? LIVESTREAM_GENRES : LIVESTREAM_LANGUAGES).map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
            {initialEvent?.livestreamIngestUrl ? (
              <div className="CreateEvent-field CreateEvent-stream-url">
                <label>Striimin lähetys-URL (vain sinulle)</label>
                {initialEvent.streamPublishActive ? (
                  <input type="text" readOnly value={initialEvent.livestreamIngestUrl} className="CreateEvent-stream-url-input" />
                ) : (
                  <p className="CreateEvent-levyraati-hint">
                    Lähetys-URL aktivoituu 10 minuuttia ennen tapahtuman alkua ja sulkeutuu tapahtuman päättyessä.
                  </p>
                )}
                {initialEvent.livestreamPlaybackPageUrl ? (
                  <p className="CreateEvent-levyraati-hint">
                    Katsojien osoite: {initialEvent.livestreamPlaybackPageUrl}
                  </p>
                ) : null}
                {initialEvent.streamPublishActive ? (
                  <p className="CreateEvent-levyraati-hint">
                    OBS: aseta Server-kenttään yllä oleva URL. Jätä Stream key tyhjäksi.
                  </p>
                ) : null}
              </div>
            ) : null}
          </>
        )}
        {eventType === 'movie' && (
          <>
            <label htmlFor="movieId" className="CreateEvent-field">
              Elokuva
            </label>
            <select
              id="movieId"
              value={movieId}
              onChange={(e) => setMovieId(e.target.value)}
              className="CreateEvent-select"
            >
              <option value="">Valitse elokuva</option>
              {movies.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.movieName || m.movie_name || m.id}
                </option>
              ))}
            </select>
          </>
        )}
        {eventType === 'levyraati' && (
          <>
            <div className="CreateEvent-field CreateEvent-levyraati-list">
              <label className="CreateEvent-field">Levyraati-lista (Audius)</label>
              <p className="CreateEvent-levyraati-hint">Kesto: {Math.floor(levyraatiTotalSeconds / 60)} min / {durationMinutes} min</p>
              <div className="CreateEvent-levyraati-add">
                <input
                  type="url"
                  value={levyraatiTrackUrl}
                  onChange={(e) => { setLevyraatiTrackUrl(e.target.value); setLevyraatiResolveError(null); }}
                  placeholder="https://audius.co/..."
                  className="CreateEvent-levyraati-url"
                />
                <button
                  type="button"
                  className="Button"
                  disabled={!levyraatiCanAdd || levyraatiResolving}
                  onClick={handleLevyraatiResolve}
                >
                  {levyraatiResolving ? 'Haetaan…' : 'Lisää'}
                </button>
              </div>
              {levyraatiResolveError && <p className="CreateEvent-error">{levyraatiResolveError}</p>}
              {levyraatiResolveHint && !levyraatiResolveError && <p className="CreateEvent-levyraati-hint" style={{ marginTop: '0.25rem', color: 'var(--color-text-muted, #666)', fontSize: '0.9rem' }}>{levyraatiResolveHint}</p>}
              <div className="CreateEvent-levyraati-add" style={{ marginTop: '0.5rem' }}>
                <button
                  type="button"
                  className="Button Button-secondary"
                  disabled={levyraatiRandomLoading || levyraatiTotalSeconds >= slotSeconds}
                  onClick={handleLevyraatiAdd10Random}
                >
                  {levyraatiRandomLoading ? 'Haetaan…' : 'Lisää 10 random biisiä'}
                </button>
              </div>
              {levyraatiPlaylist.length > 0 && (
                <ul className="CreateEvent-levyraati-tracks">
                  {levyraatiPlaylist.map((t, i) => (
                    <li key={i} className="CreateEvent-levyraati-track">
                      <span>{t.track_author} – {t.track_name}</span>
                      <span className="CreateEvent-levyraati-dur">{t.duration_seconds ? `${Math.floor(t.duration_seconds / 60)}:${String(t.duration_seconds % 60).padStart(2, '0')}` : '—'}</span>
                      <button type="button" className="Button Button-secondary CreateEvent-levyraati-remove" onClick={() => removeLevyraatiTrack(i)} aria-label="Poista">×</button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </>
        )}
        {eventType === 'keskustelu' && (
          <>
            <label className="CreateEvent-field" htmlFor="embedUrl">
              Video
            </label>
            <input
              id="embedUrl"
              type="url"
              value={embedUrl}
              onChange={(e) => setEmbedUrl(e.target.value)}
              placeholder="https://..."
            />
            {embedUrlInvalid && (
              <p className="CreateEvent-error CreateEvent-error--video">
                Vain Youtube-videot mahdollisia tällä hetkellä..
              </p>
            )}
          </>
        )}

{eventType === 'game' && (
          <>
            <label htmlFor="gameType" className="CreateEvent-field">
              Peli tapahtumassa
            </label>
            <select
              id="gameType"
              value={gameType}
              onChange={(e) => setGameType(e.target.value)}
              className="CreateEvent-select"
            >
              <option value="riddle-fi">Arvoituspeli (suomi)</option>
              <option value="trivia-fi">Tietovisa (suomi)</option>
              <option value="emoji-fi">Emoji-arvausta (suomi)</option>
              <option value="abcd-fi">ABCD-tietovisa</option>
            </select>
          </>
        )}

        <label htmlFor="eventDescription" className="CreateEvent-field">
          Kuvaus
        </label>
        <MarkdownEditor
          id="eventDescription"
          value={description}
          onChange={setDescription}
        />
        <label htmlFor="enableQA" className="CreateEvent-field">
          Q&A-osio
          </label>
          <input
            id="enableQA"
            type="checkbox"
            checked={enableQA}
            onChange={(e) => setEnableQA(e.target.checked)}
          />

        {error && <p className="CreateEvent-error">{error}</p>}
        <EventCalendarActions event={initialEvent} />
        <div className="CreateEvent-actions">
          <button type="button" className="Button CreateEvent-cancel" onClick={handleReturn}>
            Cancel
          </button>
          <button type="submit" className="Button CreateEvent-save" disabled={!formValid || saving}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </form>
    </div>
  );
}
